import { useEffect, useMemo, useState } from 'react';
import { ApiErrorText, ModalComponentWrapper } from '@/shared/components';
import { InternalTransferModalFooter } from './InternalTransferModalFooter';
import { InternalTransferForm } from './InternalTransferForm';
import { useTranslation } from 'react-i18next';
import { useDictionary } from '@/shared/api';
import type { TransferInternalSettingsDictionary } from '@/shared/types';
import {
  useInternalTransferForm,
  useInternalTransferModals,
  type InternalTransferInitialContext,
} from '../model';
import { useModalStore } from '@/shared/lib';
import { useAfterOperationRefresh } from '@/entities/operation';

type InternalTransferModalProps = {
  onClose?: () => void;
  initialContext?: InternalTransferInitialContext;
};

const InternalTransferModal = ({
  onClose,
  initialContext,
}: InternalTransferModalProps) => {
  const { t } = useTranslation();
  const [isFormDirty, setIsFormDirty] = useState(false);
  const { setCloseConfirmationRequired } = useModalStore();
  const { openConfirmationModal, openResultModal } =
    useInternalTransferModals();

  const triggerAccountsRefetch = useAfterOperationRefresh();

  const {
    data: transferInternalSettings,
    isLoading,
    isError,
    error,
  } = useDictionary<TransferInternalSettingsDictionary>({
    dictionaryName: 'transferInternalSettings',
  });

  useEffect(() => {
    setCloseConfirmationRequired(isFormDirty);
  }, [isFormDirty, setCloseConfirmationRequired]);

  const {
    isTransferring,
    form,
    watchedValues,
    accountOptions,
    selectedAccountFrom,
    selectedAccountTo,
    selectedAssetFrom,
    selectedAssetTo,
    initialFromType,
    initialToType,
    isInitFormData,
    setSelectedAssetFrom,
    setSelectedAssetTo,
    setSelectedAccountFrom,
    setSelectedAccountTo,
    handleFormSubmit,
    handleMaxClick,
    isError: isFormError,
    error: formError,
  } = useInternalTransferForm({
    onValidateSuccess: data => {
      openConfirmationModal({
        formValues: data,
        selectedAccountFrom,
        selectedAccountTo,
        selectedAssetFrom,
        selectedAssetTo,
        onSuccess: () => {
          form.reset();
          setIsFormDirty(false);
          triggerAccountsRefetch();
        },
      });
    },
    openResultModal,
    onTransferSuccess: triggerAccountsRefetch,
  });

  const { initialFromAccountType, initialToAccountType } = useMemo(() => {
    const { direction, accountType } = initialContext ?? {};
    if (direction) {
      return {
        initialFromAccountType: direction === 'from' ? accountType : undefined,
        initialToAccountType: direction === 'to' ? accountType : undefined,
      };
    }

    return {
      initialFromAccountType:
        initialContext?.accountType === 'ewallet'
          ? initialContext.accountType
          : initialFromType,
      initialToAccountType:
        initialContext && initialContext.accountType !== 'ewallet'
          ? initialContext.accountType
          : initialToType,
    };
  }, [initialContext, initialFromType, initialToType]);

  if (isLoading) {
    return <></>;
  }

  if (isError || !transferInternalSettings) {
    return (
      <div className="w-full">
        <div className="text-center p-[4rem]">
          <ApiErrorText error={error} size="small" />
        </div>
      </div>
    );
  }

  const isContinueMode =
    (!!watchedValues.assetFrom &&
      !!watchedValues.assetTo &&
      watchedValues.assetFrom !== watchedValues.assetTo) ||
    selectedAccountFrom?.type === 'leverage' ||
    selectedAccountFrom?.type === 'earn';

  return (
    <ModalComponentWrapper
      onBack={onClose}
      title={t('internalTransferModal.title')}
      contentClassName="overflow-x-hidden"
      footer={
        <InternalTransferModalFooter
          isTransferring={isTransferring}
          onSubmit={() => form.handleSubmit(handleFormSubmit)()}
          isContinueMode={isContinueMode}
        />
      }>
      <InternalTransferForm
        initialContext={initialContext}
        isInitFormData={isInitFormData}
        form={form}
        watchedValues={watchedValues}
        accountOptions={accountOptions}
        selectedAssetFrom={selectedAssetFrom}
        selectedAccountFrom={selectedAccountFrom}
        transferInternalSettings={transferInternalSettings.value}
        onMaxClick={handleMaxClick}
        onFormDirtyChange={setIsFormDirty}
        initialFromAccountType={initialFromAccountType}
        initialToAccountType={initialToAccountType}
        setSelectedAssetFrom={setSelectedAssetFrom}
        setSelectedAssetTo={setSelectedAssetTo}
        setSelectedAccountFrom={setSelectedAccountFrom}
        setSelectedAccountTo={setSelectedAccountTo}
      />
      {isFormError && formError && (
        <ApiErrorText error={formError} size="small" />
      )}
    </ModalComponentWrapper>
  );
};

export { InternalTransferModal };
